import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getBackendUrl } from '../utils/api';

const API = getBackendUrl();

// Logo URL - Fallen Dragon Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fallen-events-hub/artifacts/9a9pm8c7_Fallen%20Dragon%20Logo.jpeg";

// Forest background
const FOREST_BG = "https://images.unsplash.com/photo-1573339607881-208e75e4b267?auto=format&fit=crop&w=1920&q=80";

const Terms = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const response = await fetch(`${API}/legal/terms`);
      const data = await response.json();
      if (data.success && data.page) {
        setPageData(data.page);
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default content if no CMS data
  const defaultData = {
    title: 'Terms & Conditions',
    meta_description: 'Fallen Dragon Events Terms and Conditions - Read our terms of service, booking policy, and participant requirements.',
    company_name: 'Fallen Dragon Events',
    content: `<h2>Acceptance of Terms</h2>
<p>By accessing and using this website and our services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website or services.</p>

<h2>Services Description</h2>
<p>Fallen Dragon Events provides specialist airsoft events and hard LARP experiences. All activities involve physical exertion and inherent risks that participants must acknowledge.</p>

<h2>Booking and Payment</h2>
<ul>
<li>All bookings are subject to availability and confirmation.</li>
<li>Payment must be made in full at the time of booking unless otherwise specified.</li>
<li>Prices are displayed in GBP and include VAT where applicable.</li>
</ul>

<h2>Cancellation Policy</h2>
<ul>
<li>Cancellations made more than 14 days before the event date will receive a full refund.</li>
<li>Cancellations made between 7-14 days before the event will receive a 50% refund.</li>
<li>Cancellations made less than 7 days before the event are non-refundable.</li>
</ul>

<h2>Participant Requirements</h2>
<ul>
<li>All participants must be at least 18 years of age unless specifically stated otherwise.</li>
<li>Participants must complete and sign a liability waiver before taking part.</li>
<li>Appropriate clothing and footwear must be worn at all times.</li>
</ul>

<h2>Governing Law</h2>
<p>These Terms and Conditions are governed by the laws of the United Kingdom.</p>`
  };

  const data = pageData || defaultData;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>{data.title} | Fallen Dragon Events</title>
        <meta name="description" content={data.meta_description || defaultData.meta_description} />
      </Helmet>

      {/* Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${FOREST_BG})` }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />

      {/* Header Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Fallen Dragon Events" className="h-12 w-auto" />
          </Link>
          <div className="hidden md:flex items-center space-x-10">
            <Link to="/#about" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">About</Link>
            <Link to="/#events" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">Events</Link>
            <Link to="/#gallery" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">Gallery</Link>
            <Link to="/#contact" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">Contact</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#c9a55a] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-[#c9a55a] mb-8">{data.title}</h1>

              {/* Content */}
              <div 
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-[#c9a55a] prose-headings:font-semibold prose-headings:mt-10 prose-headings:mb-4
                  prose-h2:text-2xl
                  prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
                  prose-li:text-gray-300 prose-li:mb-2
                  prose-ul:my-4 prose-ul:pl-6
                  prose-strong:text-white
                  prose-a:text-[#c9a55a] prose-a:no-underline hover:prose-a:underline"
                dangerouslySetInnerHTML={{ __html: data.content }}
              />

              {/* Contact Section */}
              {(data.company_email || data.company_phone || data.company_address) && (
                <div className="mt-12 pt-8 border-t border-gray-800">
                  <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Contact Us</h2>
                  <div className="space-y-2 text-gray-300">
                    {data.company_email && (
                      <p>Email: <a href={`mailto:${data.company_email}`} className="text-[#c9a55a] hover:underline">{data.company_email}</a></p>
                    )}
                    {data.company_phone && (
                      <p>Telephone: <a href={`tel:${data.company_phone}`} className="text-[#c9a55a] hover:underline">{data.company_phone}</a></p>
                    )}
                    {data.company_address && <p>Address: {data.company_address}</p>}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-12 pt-8 border-t border-gray-800 text-gray-500">
                {data.effective_date && (
                  <p className="mb-2">Effective Date: {new Date(data.effective_date).toLocaleDateString('en-GB')}</p>
                )}
                <p>© {data.company_name || 'Fallen Dragon Events'}</p>
                {data.company_registration && <p>{data.company_registration}</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Terms;
